import type { ContextId } from '../../store/slices/helpSlice';

export type HelpTopicCategory =
  | 'getting_started'
  | 'screening'
  | 'charts'
  | 'deep_analysis'
  | 'favorites_alerts'
  | 'agents'
  | 'account_legal';

export interface HelpTopic {
  id: string;
  category: HelpTopicCategory;
  title: string;
  summary: string;
  body: string[];
  quickActions?: Array<{ label: string; href?: string; contextId?: ContextId }>;
}

export const HELP_TOPICS: HelpTopic[] = [
  {
    id: 'start_here',
    category: 'getting_started',
    title: 'Start Here',
    summary: 'The fastest way to use the app in your first session.',
    body: [
      'Open Filters and choose a clear market + strategy scope.',
      'Run Screening and review top-ranked results.',
      'Open Charts and Deep Analysis for validation before saving favorites.',
    ],
  },
  {
    id: 'screening_filters',
    category: 'screening',
    title: 'Screening & Filters',
    summary: 'How to run scans effectively with fewer false positives.',
    body: [
      'Start with 2-4 high-impact filters instead of enabling everything.',
      'Use Advanced Filters when you need tighter universe control.',
      'Re-run scans after meaningful filter changes and compare results.',
    ],
  },
  {
    id: 'ma_150_strategy',
    category: 'screening',
    title: 'The 150 MA System',
    summary: 'Rule-based entry/exit anchored on the 150-day simple moving average.',
    body: [
      'Rule 1 - Entry: price must sit on or above the SMA-150. Never buy below the line.',
      'Rule 2 - Trend: the SMA-150 must be flat or sloping up. A down-sloping line means the 150-day trend is still negative; stand aside.',
      'Rule 3 - Exit: the moment a daily close prints below SMA-150, you are out. No averaging down, no hoping, no "retesting the line".',
      'Why it works: buying next to the line caps downside near ~2% while upside stays open-ended. The math is asymmetric, not the prediction.',
      'This is a signal system, not a forecast. You do not need to be right about the company - you need to respect the line.',
    ],
  },
  {
    id: 'ma_150_vs_200',
    category: 'screening',
    title: '150 MA vs 200 MA - when to switch',
    summary: 'SMA-150 is the default. Some stocks fit SMA-200 better.',
    body: [
      'SMA-150 is the default signal line for regular momentum stocks.',
      'Switch to SMA-200 for slower, steadier names whose price rides a wide uptrend with low noise (examples: ride-share or health names that grind rather than spike).',
      'Rule: if the SMA-150 is producing frequent fake-out exits on a stock that keeps trending, test the SMA-200 - the wider average filters more noise at the cost of a looser stop.',
      'Never run both at once on the same position. Pick one line per ticker and follow it.',
    ],
  },
  {
    id: 'risk_staircase',
    category: 'screening',
    title: 'Risk management: staircase stop-loss',
    summary: 'Lock gains on parabolic moves without cutting early winners.',
    body: [
      'Initial stop = a daily close below the SMA-150 (or 200, if that is your line).',
      'Each time price jumps ~10% above your last stop level, ratchet the stop up one step. The move becomes a staircase of locked-in gains.',
      'When price extends 15–20% above the moving average, start active stop management rather than waiting for the line.',
      'Ahead of earnings on a volatile momentum stock, prefer to exit before the report. On a heavy, steady stock close to its SMA, you may hold.',
      'No one ever went broke taking profits off a working trade.',
    ],
  },
  {
    id: 'false_breakout_tax',
    category: 'screening',
    title: 'False breakouts are the tax',
    summary: 'Expect some losing re-entries. The rules still win over time.',
    body: [
      'Some entries will trigger, stop out below the line, then reverse back above. That is a fake breakout, not a broken system.',
      'Do not try to outsmart the rule. Take the small loss (~2%) and re-enter when the setup re-qualifies.',
      'Trying to hold through the break - "to be clever" - is how small losses become catastrophic drawdowns.',
      'Over time, disciplined re-entries still net an edge because the winners run far further than the fakes cost.',
    ],
  },
  {
    id: 'charts_workflow',
    category: 'charts',
    title: 'Charts Workflow',
    summary: 'Use chart view for fast validation and shortlist cleanup.',
    body: [
      'Overlay SMA-150 (and SMA-200 if it is your chosen line) on every chart. Price relative to the line is the core signal.',
      'Entry candidates: price sitting on or just above the SMA-150 with the line flat or rising.',
      'Rejection candidates: price under the line, or the line sloping down - move on fast.',
      'Use the multi-chart comparison view to scan shortlists side-by-side across different timeframes without switching windows.',
      'Watch the volume pane for confirmation on MA touches and breakouts.',
      'Draw horizontal support/resistance from recent pivots; they compound the MA signal at confluence points.',
      'Set MA-cross alerts here (or in TradingView) instead of staring at the screen. Never use general news sites to drive entries.',
      'From a clean setup, jump to Deep Analysis to confirm pattern confluence before acting.',
    ],
  },
  {
    id: 'deep_analysis_overview',
    category: 'deep_analysis',
    title: 'Deep Analysis',
    summary: 'Structure, indicators, pattern confidence, and MA-based risk in one place.',
    body: [
      'Run Deep Analysis after screening narrows the list - not before, when the universe is still noisy.',
      'MA Pattern card: renders only when one of the tracked MA patterns triggers on this ticker. An empty card means no pattern fired, which is itself information.',
      'Position Checkup: verifies the three entry rules are still true right now - price ≥ SMA-150, line flat/rising, no close below.',
      'Structured output covers sector-weighted valuation, indicator alignment, and pattern confidence in one panel.',
      'AI summaries use the Perplexity Sonar model; treat them as context, never as trading advice.',
      'Disclaimers are multilingual and always present. The platform is an information tool, not a financial advisor.',
    ],
  },
  {
    id: 'favorites_and_alerts',
    category: 'favorites_alerts',
    title: 'Favorites',
    summary: 'Move from one-time scans to a focused, repeatable watchlist.',
    body: [
      'Add only high-conviction symbols to keep watchlists focused.',
      'Re-run Deep Analysis on favorites as price action evolves.',
    ],
  },
  {
    id: 'account_and_legal',
    category: 'account_legal',
    title: 'Account, Billing & Legal',
    summary: 'Find support, policy links, and important disclaimers.',
    body: [
      'Access legal documents from the footer at any time.',
      'Contact support for billing or account issues.',
    ],
  },
];

export const HELP_CATEGORIES: Array<{
  id: HelpTopicCategory;
  label: string;
}> = [
  { id: 'getting_started', label: 'Getting Started' },
  { id: 'screening', label: 'Screening & Filters' },
  { id: 'charts', label: 'Charts' },
  { id: 'deep_analysis', label: 'Deep Analysis' },
  { id: 'favorites_alerts', label: 'Favorites' },
  { id: 'account_legal', label: 'Account & Legal' },
];
